import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { SHOP, reviewBase } from "../shop";

// ─── Colour tokens (warm brand palette) ──────────────────────────────────────

const BRAND = SHOP.accent;
const REVIEW_BASE = `${reviewBase()}/review`;
const SAMPLE_LINK = `${REVIEW_BASE}/preview-sample-id`;
// Beispieltext fuer {{HINWEIS}} in der Vorschau — im Echtbetrieb kommt er
// aus draft_candidates.customerNotice.
const SAMPLE_NOTICE =
  "Dein Spruch ist etwas laenger als üblich. Damit er vollstaendig und gut " +
  "lesbar auf das Produkt passt, haben wir ihn in einer anderen Schriftart " +
  "gesetzt.";

// ─── Default German email texts ───────────────────────────────────────────────

export const EMAIL_TEXT_DEFAULTS = {
  fromLocal: "gestaltung",
  reminder1Days: 2,
  reminder2Days: 5,
  reminder3Days: 8,

  initialSubject:
    "Entwurf für deine Etsy Bestellung ist fertig. Bitte kurzfristig prüfen!",
  initialBody:
    "Hallo {{NAME}},\n" +
    "dein persönlicher Entwurf ist fertig ✨\n" +
    "👉 Bitte prüfe ihn jetzt hier:\n" +
    "{{LINK}}\n" +
    "Bitte nimm dir einen Moment Zeit (2-3 min) und prüfe deinen Entwurf sorgfältig.\n" +
    "Er wird genauso produziert, wie du ihn siehst (nur Deko wird nachträglich angebracht).\n" +
    "\n" +
    "Sobald du den Entwurf bestätigst, starten wir direkt mit der Produktion.\n" +
    "Wenn du noch etwas ändern möchtest, kannst du uns das ganz einfach über die Seite mitteilen.\n" +
    "Bei Fragen sind wir jederzeit für dich da unter " + SHOP.email + "\n" +
    "Liebe Grüße\n" +
    "Dein Team von " + SHOP.name,

  reminder1Subject: "Erinnerung: Dein Entwurf wartet auf dich 💛",
  reminder1Body:
    "Hallo {{NAME}},\n" +
    "wir wollten dich kurz daran erinnern, dass dein persönlicher Entwurf noch auf deine Prüfung wartet.\n" +
    "👉 Hier kannst du deinen Entwurf ansehen:\n" +
    "{{LINK}}\n" +
    "Die Prüfung dauert nur wenige Minuten – danach können wir direkt mit der Produktion starten.\n" +
    "Wenn du Änderungswünsche hast, kannst du uns diese ganz einfach über die Seite mitteilen.\n" +
    "Bei Fragen sind wir jederzeit gerne für dich da.\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  reminder2Subject: "Bitte kurz prüfen: Dein Entwurf ist noch offen",
  reminder2Body:
    "Hallo {{NAME}},\n" +
    "dein Entwurf wurde bereits erstellt, aber wir haben noch keine Rückmeldung von dir erhalten.\n" +
    "👉 Bitte prüfe deinen Entwurf hier:\n" +
    "{{LINK}}\n" +
    "Sobald du ihn freigibst, starten wir direkt mit der Produktion 🚀\n" +
    "Falls du Änderungen wünschst, kannst du diese einfach über die Seite eintragen.\n" +
    "Hinweis: Ohne Freigabe können wir deine Bestellung leider nicht weiter bearbeiten und die Lieferzeit verlängert sich.\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  reminder3Subject: "Letzte Erinnerung: Bitte Entwurf freigeben",
  reminder3Body:
    "Hallo {{NAME}},\n" +
    "wir haben bisher noch keine Rückmeldung zu deinem Entwurf erhalten.\n" +
    "👉 Bitte prüfe und bestätige deinen Entwurf hier:\n" +
    "{{LINK}}\n" +
    "Ohne deine Freigabe können wir die Produktion nicht starten.\n" +
    "Sollten wir keine Rückmeldung erhalten, kann es sein, dass die Bestellung storniert werden muss.\n" +
    "Wenn du Fragen hast oder Unterstützung benötigst, melde dich jederzeit gerne bei uns 💛\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  approvedSubject: "Vielen Dank – dein Entwurf ist freigegeben!",
  approvedBody:
    "Hallo {{NAME}},\n" +
    "vielen Dank für deine Freigabe! 🎉\n" +
    "Wir haben deine Bestätigung erhalten und starten jetzt direkt mit der Produktion.\n" +
    "Du erhältst deine Bestellung so schnell wie möglich.\n" +
    "\n" +
    "Bei Fragen stehen wir dir jederzeit gerne zur Verfügung:\n" +
    SHOP.email + "\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  changeSubject: "Wir haben deinen Änderungswunsch erhalten",
  changeBody:
    "Hallo {{NAME}},\n" +
    "vielen Dank für dein Feedback! 💛\n" +
    "Wir haben folgende Änderungswünsche von dir erhalten:\n" +
    "{{FEEDBACK}}\n" +
    "\n" +
    "Wir kümmern uns so schnell wie möglich darum.\n" +
    "In der Regel erstellen wir den überarbeiteten Entwurf innerhalb von 1–2 Werktagen\n" +
    "und senden ihn dir erneut zur Prüfung zu.\n" +
    "\n" +
    "Bei Fragen erreichst du uns jederzeit unter " + SHOP.email + "\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  emailFooter: "© " + SHOP.name + " · " + SHOP.email,

  etsy2Reminder:
    "Hallo {{NAME}},\n" +
    "wir wollten kurz nachfragen, ob du unsere E-Mails zum Entwurf für Bestellung {{ORDER}} erhalten hast.\n" +
    "Bitte prüfe auch deinen Spam-Ordner – dort landen unsere Nachrichten manchmal.\n" +
    "Bei Fragen kannst du uns hier direkt antworten.\n" +
    "Liebe Grüße, dein " + SHOP.name + " Team",

  etsy3Reminder:
    "Hallo {{NAME}},\n" +
    "letzte Erinnerung: dein persönlicher Entwurf für Bestellung {{ORDER}} wartet noch auf deine Freigabe.\n" +
    "Ohne Rückmeldung können wir die Produktion leider nicht starten.\n" +
    "Bitte melde dich kurz – entweder hier oder unter " + SHOP.email + ".\n" +
    "Liebe Grüße, dein " + SHOP.name + " Team",
};

// ─── HTML rendering helpers ───────────────────────────────────────────────────

const linkButton = (href) =>
  `<p style="text-align:center;margin:20px 0;">` +
  `<a href="${href}" style="background:${BRAND};color:#ffffff;` +
  `padding:12px 28px;border-radius:6px;text-decoration:none;` +
  `font-weight:bold;font-size:15px;display:inline-block;">` +
  `Entwurf jetzt prüfen →</a></p>`;

const emailWrapper = (bodyHtml, footer) =>
  `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#fdf7f4;` +
  `font-family:Arial,sans-serif;">` +
  `<table width="100%" cellpadding="0" cellspacing="0">` +
  `<tr><td align="center" style="padding:32px 16px;">` +
  `<table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;` +
  `border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,0.08);overflow:hidden;">` +
  `<tr><td style="background:${BRAND};padding:20px 32px;">` +
  `<span style="color:#ffffff;font-size:18px;font-weight:bold;">${SHOP.name}</span>` +
  `</td></tr>` +
  `<tr><td style="padding:32px;">${bodyHtml}</td></tr>` +
  `<tr><td style="padding:16px 32px;border-top:1px solid #e8d8d0;` +
  `font-size:12px;color:#a0aec0;text-align:center;">` +
  `${footer || `© ${SHOP.name} · ${SHOP.email}`}` +
  `</td></tr>` +
  `</table></td></tr></table></body></html>`;

const buildEmailHtml = (bodyText, name, link, order, feedback, footer, notice) => {
  const button = linkButton(link);
  const feedbackBlock = feedback
    ? `<div style="background:#fdf7f4;border-left:3px solid #B8A09A;` +
      `padding:10px 14px;margin:8px 0;color:#2d3748;font-style:italic;">` +
      `${feedback}</div>`
    : "";
  const noticeBlock = notice
    ? `<div style="background:#fff8e1;border-left:3px solid #e6a545;` +
      `padding:12px 14px;margin:12px 0;color:#5c4408;line-height:1.5;">` +
      `<strong>Hinweis zu deinem Entwurf:</strong><br>${notice}</div>`
    : "";
  const lines = bodyText
    .replace(/\{\{NAME\}\}/g, name || "Kunde")
    .replace(/\{\{ORDER\}\}/g, order || "")
    .split("\n");
  const parts = lines.map((line) => {
    if (line.trim() === "{{LINK}}") return button;
    if (line.trim() === "{{FEEDBACK}}") return feedbackBlock;
    if (line.trim() === "{{HINWEIS}}") return noticeBlock;
    if (line.trim() === "") return `<div style="height:10px;"></div>`;
    return `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">${line}</p>`;
  });
  return emailWrapper(parts.join(""), footer);
};

// ─── Shared input/textarea styles ─────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.78rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.5em 0.75em", border: "1px solid #e8d8d0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.1em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { key: "initial",   label: "Erste Email",          subjectKey: "initialSubject",  bodyKey: "initialBody" },
  { key: "reminder1", label: "1. Erinnerung",         subjectKey: "reminder1Subject", bodyKey: "reminder1Body" },
  { key: "reminder2", label: "2. Erinnerung",         subjectKey: "reminder2Subject", bodyKey: "reminder2Body" },
  { key: "reminder3", label: "3. Erinnerung",         subjectKey: "reminder3Subject", bodyKey: "reminder3Body" },
  { key: "approved",  label: "Bestätigung: Freigabe", subjectKey: "approvedSubject", bodyKey: "approvedBody" },
  { key: "change",    label: "Bestätigung: Änderung", subjectKey: "changeSubject",   bodyKey: "changeBody" },
  { key: "etsy2",     label: "Etsy 2. Erinnerung",   type: "etsy", bodyKey: "etsy2Reminder" },
  { key: "etsy3",     label: "Etsy 3. Erinnerung",   type: "etsy", bodyKey: "etsy3Reminder" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const EmailPreview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode,  setEditMode]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [form,      setForm]      = useState(null); // null = loading

  // Sample data for preview (live-editable)
  const [firstName,       setFirstName]       = useState("Sarah");
  const [orderNumber,     setOrderNumber]     = useState("10042");
  const [previewFeedback, setPreviewFeedback] = useState(
    "Bitte \u00e4ndere den Namen auf \u201eFamilie M\u00fcller\u201c.",
  );

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "emailTexts"));
      setForm(snap.exists()
        ? { ...EMAIL_TEXT_DEFAULTS, ...snap.data() }
        : { ...EMAIL_TEXT_DEFAULTS });
    };
    load();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "emailTexts"), form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(false);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade E-Mail-Konfiguration…
      </p>
    );
  }

  const tabStyle = (active) => ({
    padding: "0.45em 1.1em", borderRadius: "6px",
    border: `1px solid ${active ? BRAND : "#e8d8d0"}`,
    background: active ? BRAND : "#fff",
    color: active ? "#fff" : "#4a5568",
    fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
  });

  // ── Edit mode ────────────────────────────────────────────────────────────────
  if (editMode) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Banner */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#ebf8ff", border: "1px solid #90cdf4",
          borderRadius: "8px", padding: "0.75em 1.25em",
          marginBottom: "1.5em", gap: "1em",
        }}>
          <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
            ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-Emails aus.
          </span>
          <button onClick={() => setEditMode(false)} style={{
            padding: "0.35em 0.9em", borderRadius: "6px",
            border: "1px solid #90cdf4", background: "#fff",
            color: "#2c5282", fontSize: "0.8rem", fontWeight: "600", cursor: "pointer",
          }}>
            ← Vorschau
          </button>
        </div>

        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            E-Mail-Texte bearbeiten
          </h3>

          {/* Global settings: footer + reminder schedule */}
          <div style={{
            background: "#fdf7f4", border: "1px solid #e8d8d0",
            borderRadius: "8px", padding: "1.1em 1.25em", marginBottom: "1.75em",
          }}>
            <p style={{ margin: "0 0 0.75em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Globale Einstellungen
            </p>

            {/* Sender address */}
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Absender-Adresse
            </label>
            <div style={{ display: "flex", alignItems: "center",
              gap: "0.4em", marginBottom: "1em" }}>
              <input
                style={{ ...inputStyle, width: "160px", marginBottom: 0 }}
                value={form.fromLocal || ""}
                onChange={(e) => setField("fromLocal", e.target.value)}
                placeholder="gestaltung"
              />
              <span style={{ color: "#4a5568", fontSize: "0.875rem",
                whiteSpace: "nowrap" }}>
                @{SHOP.email.split("@")[1]}
              </span>
            </div>

            {/* Footer text */}
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              E-Mail-Footer
            </label>
            <input
              style={{ ...inputStyle, marginBottom: "1em" }}
              value={form.emailFooter || ""}
              onChange={(e) => setField("emailFooter", e.target.value)}
            />

            {/* Reminder days */}
            <p style={{ margin: "0 0 0.5em", fontWeight: "600",
              fontSize: "0.8rem", color: "#4a5568",
              textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Erinnerungs-Zeitplan (Tage nach Erstellung)
            </p>
            <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
              {[1, 2, 3].map((n) => (
                <label key={n} style={{ display: "flex", alignItems: "center",
                  gap: "0.5em", fontSize: "0.875rem", color: "#4a5568" }}>
                  <span style={{ fontWeight: "600" }}>{n}. Erinnerung nach</span>
                  <input
                    type="number" min="1"
                    value={form[`reminder${n}Days`]}
                    onChange={(e) =>
                      setField(`reminder${n}Days`, Number(e.target.value))}
                    style={{
                      width: "60px", padding: "0.3em 0.5em",
                      border: "1px solid #e8d8d0", borderRadius: "5px",
                      fontSize: "0.875rem", textAlign: "center",
                    }}
                  />
                  <span>Tagen</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tab selector for editing */}
          <div style={{ display: "flex", gap: "0.5em", marginBottom: "1.5em" }}>
            {TABS.map((t, i) => (
              <button key={t.key} style={tabStyle(activeTab === i)}
                onClick={() => setActiveTab(i)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Subject / Body — branched by tab type */}
          {TABS[activeTab].type === "etsy" ? (
            <>
              <div style={{
                background: "#fffbeb", border: "1px solid #f6e05e",
                borderRadius: "6px", padding: "0.6em 0.9em", marginBottom: "1em",
                fontSize: "0.82rem", color: "#744210",
              }}>
                ℹ️ Diese Nachricht wird als Etsy Conversation an den Käufer gesendet — kein HTML, kein Betreff, nur Klartext.
              </div>
              <label style={labelStyle}>
                {"Text — Platzhalter: {{NAME}}, {{ORDER}}"}</label>
              <textarea
                style={{ ...taStyle, minHeight: "200px" }}
                value={form[TABS[activeTab].bodyKey] || ""}
                onChange={(e) => setField(TABS[activeTab].bodyKey, e.target.value)}
              />
            </>
          ) : (
            <>
              <label style={labelStyle}>Betreff</label>
              <input
                style={inputStyle}
                value={form[TABS[activeTab].subjectKey] || ""}
                onChange={(e) => setField(TABS[activeTab].subjectKey, e.target.value)}
              />
              <label style={labelStyle}>
                {TABS[activeTab].key === "change"
                  ? "Text — Platzhalter: {{NAME}}, {{LINK}}, {{ORDER}} und {{FEEDBACK}}"
                  : TABS[activeTab].key === "initial"
                    ? "Text — Platzhalter: {{NAME}}, {{LINK}}, {{ORDER}} und {{HINWEIS}}"
                    : "Text — Platzhalter: {{NAME}}, {{LINK}} und {{ORDER}}"}
              </label>
              <textarea
                style={{ ...taStyle, minHeight: "260px" }}
                value={form[TABS[activeTab].bodyKey] || ""}
                onChange={(e) => setField(TABS[activeTab].bodyKey, e.target.value)}
              />
            </>
          )}

          <button onClick={handleSave} disabled={saving} style={{
            padding: "0.65em 2em", borderRadius: "7px",
            border: "none", background: saving ? "#a0aec0" : BRAND,
            color: "#fff", fontWeight: "700", fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>
    );
  }

  // ── Preview mode ──────────────────────────────────────────────────────────────
  const currentTab = TABS[activeTab];
  const isChangeTab = currentTab.key === "change";
  const applyPlaceholders = (text) => text
    .replace(/\{\{NAME\}\}/g, firstName || "Kunde")
    .replace(/\{\{ORDER\}\}/g, orderNumber || "")
    .replace(/\{\{FEEDBACK\}\}/g, isChangeTab ? previewFeedback : "");
  const isInitialTab = currentTab.key === "initial";
  const previewHtml = buildEmailHtml(
    form[currentTab.bodyKey],
    firstName,
    SAMPLE_LINK,
    orderNumber,
    isChangeTab ? previewFeedback : "",
    form.emailFooter,
    isInitialTab ? SAMPLE_NOTICE : "",
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em", gap: "1em" }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Email Vorschau
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
          {saved && (
            <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
              ✓ Gespeichert!
            </span>
          )}
          <button onClick={() => setEditMode(true)} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #e8d8d0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
      </div>

      {/* Reminder schedule + sample data bar */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "1em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "2em", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        {/* Reminder days (read-only) */}
        <div>
          <div style={{ fontWeight: "700", fontSize: "0.78rem", color: "#4a2c2a",
            textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4em" }}>
            Erinnerungs-Zeitplan
          </div>
          <div style={{ display: "flex", gap: "1.25em", fontSize: "0.85rem", color: "#4a5568" }}>
            {[1, 2, 3].map((n) => (
              <span key={n}>
                <strong>{n}.</strong> nach {form[`reminder${n}Days`]} Tagen
              </span>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: "1px", background: "#e8d8d0", alignSelf: "stretch" }} />

        {/* Sample data */}
        <div style={{ display: "flex", gap: "1.25em", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>Vorschau-Name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
              placeholder="z.B. Sarah"
            />
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>Bestellnummer</label>
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              style={{ ...inputStyle, width: "130px", marginBottom: 0 }}
              placeholder="z.B. 10042"
            />
          </div>
          {isChangeTab && (
            <div>
              <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
                Änderungstext (Vorschau)
              </label>
              <input
                value={previewFeedback}
                onChange={(e) => setPreviewFeedback(e.target.value)}
                style={{ ...inputStyle, width: "260px", marginBottom: 0 }}
                placeholder="z.B. Bitte Namen ändern…"
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: "0.5em", marginBottom: "1em" }}>
        {TABS.map((t, i) => (
          <button key={t.key} style={tabStyle(activeTab === i)}
            onClick={() => setActiveTab(i)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Subject line + email iframe — or Etsy message preview */}
      {currentTab.type === "etsy" ? (
        <div style={{
          border: "1px solid #e8d8d0", borderRadius: "8px", overflow: "hidden",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        }}>
          <div style={{
            background: "#f7f7f7", borderBottom: "1px solid #e2e8f0",
            padding: "0.75em 1.25em", fontSize: "0.82rem",
            color: "#718096", fontWeight: "600",
          }}>
            Etsy Nachricht (Vorschau) — Konversation an Käufer
          </div>
          <div style={{ background: "#fff", padding: "1.5em" }}>
            <div style={{
              background: "#f7f7f7", borderRadius: "12px",
              padding: "1em 1.25em", maxWidth: "480px",
              fontSize: "0.875rem", color: "#2d3748",
              whiteSpace: "pre-line", lineHeight: "1.6",
            }}>
              {applyPlaceholders(form[currentTab.bodyKey] || "")}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#a0aec0", marginTop: "0.5em" }}>
              {SHOP.name} · gerade eben
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{
            background: "#fff", borderRadius: "8px 8px 0 0",
            border: "1px solid #e8d8d0", borderBottom: "none",
            padding: "0.75em 1.25em", fontSize: "0.875rem", color: "#4a5568",
          }}>
            <span style={{ fontWeight: "600", color: "#2d3748" }}>Betreff: </span>
            {applyPlaceholders(form[currentTab.subjectKey] || "")}
          </div>
          <iframe
            key={`${activeTab}-${firstName}-${JSON.stringify(form[currentTab.bodyKey])}`}
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
            style={{
              width: "100%", height: "680px",
              border: "1px solid #e8d8d0", borderRadius: "0 0 8px 8px",
              boxShadow: "0 1px 4px rgba(74,44,42,0.08)", display: "block",
            }}
            title={`Email Vorschau: ${currentTab.label}`}
          />
        </>
      )}
    </div>
  );
};

export default EmailPreview;
